/**
 * 통합관리자 — 매출 현황
 * ---------------------------------------------------------------------------
 * ## 왜 과금 화면과 따로 두나
 * 「과금」 화면은 **일을 하는 곳**이다 — 청구서를 발행하고 수납을 기록한다.
 * 이 화면은 **보는 곳**이다. 둘을 한 화면에 두면 숫자를 보러 들어왔다가
 * 실수로 청구서를 발행하게 된다.
 *
 * ## 세 갈래로 나눈다
 *   확정 매출   발행한 청구서의 합. 회계가 보는 숫자
 *   수납        실제로 들어온 돈. 자금이 보는 숫자
 *   예상 매출   지금 구독이 유지될 때 매달 들어올 금액(MRR)
 *
 * 확정 매출과 수납을 섞으면 "11월에 얼마 벌었나" 와 "11월에 얼마 받았나" 가
 * 뭉개진다. 9월 청구서를 10월에 받으면 둘은 다른 달의 숫자다.
 *
 * ## 미수금은 기간과 무관하다
 * 「이번 달 미수금」은 말이 안 된다. 안 들어온 돈은 언제 발행했든 지금
 * 안 들어온 돈이다. 조회 기간을 바꿔도 미수금 칸은 움직이지 않는다.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Progress,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { DownloadOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';
import { downloadCsv, csvDate } from '../../lib/csv';
import { PageHelp } from '../../components/PageHelp';
import { GUIDE } from '../../lib/guide';

const { Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

interface RevenueSummary {
  from: string;
  to: string;
  invoicedAmount: number;
  invoicedCount: number;
  collectedAmount: number;
  /** 그 기간에 발행한 청구서가 지금까지 걷힌 비율 (0~100). 발행이 없으면 null */
  collectRate: number | null;
  outstandingAmount: number;
  overdueAmount: number;
  overdueCount: number;
  mrr: number;
  trialMrr: number;
  payingTenants: number;
  trialTenants: number;
}

interface RevenueMonthly {
  month: string;
  invoicedAmount: number;
  collectedAmount: number;
}

interface RevenueByProduct {
  productCode: string;
  productName: string;
  /** 조회 기간에 실제로 청구한 금액(공급가액) */
  invoicedAmount: number;
  mrr: number;
  trialMrr: number;
  tenants: number;
  trialTenants: number;
}

interface OverdueTenant {
  tenantId: string;
  companyName: string;
  slug: string;
  status: string;
  overdueAmount: number;
  overdueDays: number;
  oldestDueAt: string;
  invoiceCount: number;
  autoSuspendedAt: string | null;
}

const won = (n: number) => `${Math.round(n).toLocaleString('ko-KR')}원`;

export default function RevenuePage() {
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);

  const params = {
    from: range[0].format('YYYY-MM-DD'),
    to: range[1].format('YYYY-MM-DD'),
  };

  const summary = useQuery<RevenueSummary, NormalizedError>({
    queryKey: ['admin', 'revenue', 'summary', params.from, params.to],
    queryFn: async () =>
      (await apiClient.get<RevenueSummary>('/admin/billing/revenue/summary', { params })).data,
  });

  const monthly = useQuery<RevenueMonthly[], NormalizedError>({
    queryKey: ['admin', 'revenue', 'monthly'],
    queryFn: async () =>
      (await apiClient.get<RevenueMonthly[]>('/admin/billing/revenue/monthly')).data,
  });

  // 기간을 요약과 같이 넘긴다. 위의 기간을 바꿨는데 이 표만 다른 기간을
  // 보고 있으면 두 숫자가 왜 안 맞는지 알 수 없다.
  const byProduct = useQuery<RevenueByProduct[], NormalizedError>({
    queryKey: ['admin', 'revenue', 'by-product', params.from, params.to],
    queryFn: async () =>
      (await apiClient.get<RevenueByProduct[]>('/admin/billing/revenue/by-product', { params }))
        .data,
  });

  const overdue = useQuery<OverdueTenant[], NormalizedError>({
    queryKey: ['admin', 'revenue', 'overdue'],
    queryFn: async () =>
      (await apiClient.get<OverdueTenant[]>('/admin/billing/revenue/overdue')).data,
  });

  const s = summary.data;
  /*
   * 수납률은 **서버가 센다.**
   *
   * 예전에는 화면에서 `수납 ÷ 확정 매출` 로 구했다. 둘은 모집단이 다르다 —
   * 9월분을 10월에 받으면 10월 수납에는 들어가지만 10월 발행액에는 없다.
   * 그래서 비율이 100% 를 넘기도 했고, 넘은 달에는 막대가 꽉 찬 채 뜻을
   * 잃었다. 서버는 「그 기간에 발행한 청구서가 얼마나 걷혔는가」로 센다.
   */
  const collectRate = s?.collectRate ?? null;

  const refreshAll = () => {
    void summary.refetch();
    void monthly.refetch();
    void byProduct.refetch();
    void overdue.refetch();
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card
        size="small"
        title={
          <Space size={6}>
            <span>매출 현황</span>
            <PageHelp title="매출 현황 — 화면 설명">{GUIDE.revenue?.body}</PageHelp>
          </Space>
        }
        extra={
          <Space>
            <RangePicker
              value={range}
              onChange={(v) => v?.[0] && v?.[1] && setRange([v[0], v[1]])}
              allowClear={false}
              presets={[
                { label: '이번 달', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
                {
                  label: '지난달',
                  value: [
                    dayjs().subtract(1, 'month').startOf('month'),
                    dayjs().subtract(1, 'month').endOf('month'),
                  ],
                },
                { label: '올해', value: [dayjs().startOf('year'), dayjs().endOf('year')] },
              ]}
            />
            <Button icon={<ReloadOutlined />} onClick={refreshAll}>
              새로고침
            </Button>
          </Space>
        }
      >
        {summary.isError && <Alert type="error" showIcon message={errorText(summary.error)} />}

        <Row gutter={[16, 16]}>
          <Col xs={12} md={6}>
            <Statistic
              title={`확정 매출 (${params.from} ~ ${params.to})`}
              value={s?.invoicedAmount ?? 0}
              suffix="원"
              loading={summary.isLoading}
              formatter={(v) => Math.round(Number(v)).toLocaleString('ko-KR')}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              청구서 {s?.invoicedCount ?? 0}건 발행
            </Text>
          </Col>
          <Col xs={12} md={6}>
            <Statistic
              title="수납"
              value={s?.collectedAmount ?? 0}
              suffix="원"
              valueStyle={{ color: '#3f8600' }}
              loading={summary.isLoading}
              formatter={(v) => Math.round(Number(v)).toLocaleString('ko-KR')}
            />
            {collectRate !== null && (
              <Tooltip title="이 기간에 발행한 청구서가 지금까지 걷힌 비율입니다. 위의 「수납」과는 다릅니다 — 수납에는 지난달 청구서를 이번 달에 받은 돈도 들어갑니다.">
                <Progress percent={collectRate} size="small" />
              </Tooltip>
            )}
          </Col>
          <Col xs={12} md={6}>
            <Statistic
              title="예상 매출 (월)"
              value={s?.mrr ?? 0}
              suffix="원"
              loading={summary.isLoading}
              formatter={(v) => Math.round(Number(v)).toLocaleString('ko-KR')}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              유료 {s?.payingTenants ?? 0}곳 · 체험 {s?.trialTenants ?? 0}곳
              {s && s.trialMrr > 0 && ` (체험 전환 시 +${won(s.trialMrr)})`}
            </Text>
          </Col>
          <Col xs={12} md={6}>
            <Statistic
              title="미수금"
              value={s?.outstandingAmount ?? 0}
              suffix="원"
              valueStyle={{ color: (s?.overdueAmount ?? 0) > 0 ? '#cf1322' : undefined }}
              loading={summary.isLoading}
              formatter={(v) => Math.round(Number(v)).toLocaleString('ko-KR')}
            />
            <Text type={(s?.overdueAmount ?? 0) > 0 ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
              그중 연체 {won(s?.overdueAmount ?? 0)} ({s?.overdueCount ?? 0}건)
            </Text>
          </Col>
        </Row>

        <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
          <b>확정 매출</b>은 그 기간에 <b>발행</b>한 청구서의 합이고, <b>수납</b>은 그 기간에{' '}
          <b>들어온</b> 돈입니다. 9월분을 10월에 받으면 서로 다른 달에 잡힙니다.{' '}
          <b>미수금</b>은 기간과 무관하게 지금 안 들어온 돈 전부입니다.
        </Paragraph>
      </Card>

      {/* ───────────────────── 월별 추이 ───────────────────── */}
      <Card
        size="small"
        title="월별 추이"
        extra={
          <Button
            icon={<DownloadOutlined />}
            onClick={() =>
              downloadCsv('월별매출', monthly.data ?? [], [
                { header: '월', value: (r) => r.month },
                { header: '확정매출', value: (r) => Math.round(r.invoicedAmount) },
                { header: '수납', value: (r) => Math.round(r.collectedAmount) },
                // 「미수」가 아니다. 발행과 수납은 모집단이 달라, 둘의 차는
                // 그달에 못 받은 돈이 아니라 **흐름의 차이**다.
                {
                  header: '발행-수납 차',
                  value: (r) => Math.round(r.invoicedAmount - r.collectedAmount),
                },
              ])
            }
          >
            엑셀 내보내기
          </Button>
        }
      >
        <Table<RevenueMonthly>
          rowKey="month"
          size="small"
          pagination={false}
          loading={monthly.isLoading}
          dataSource={monthly.data ?? []}
          scroll={{ y: 320 }}
          columns={[
            { title: '월', dataIndex: 'month', width: 110 },
            {
              title: '확정 매출',
              dataIndex: 'invoicedAmount',
              align: 'right',
              render: (v: number) => won(v),
            },
            {
              title: '수납',
              dataIndex: 'collectedAmount',
              align: 'right',
              render: (v: number) => <Text type="success">{won(v)}</Text>,
            },
            {
              /*
               * 「미수」가 아니다. 발행과 수납은 모집단이 다르다 — 9월분을
               * 10월에 받으면 10월은 수납이 더 커져 음수가 된다. 그것을
               * 「미수 −100만원」이라고 적으면 거짓말이 된다. 이름과 설명을
               * 흐름의 차이로 둔다.
               */
              title: (
                <Tooltip title="그달 발행액에서 그달 수납액을 뺀 값입니다. 미수금이 아닙니다 — 지난달 청구서를 이번 달에 받으면 음수가 됩니다.">
                  발행 − 수납
                </Tooltip>
              ),
              key: 'gap',
              align: 'right',
              render: (_, r) => {
                const gap = r.invoicedAmount - r.collectedAmount;
                if (gap === 0) return <Text type="secondary">—</Text>;
                return (
                  <Text type={gap > 0 ? 'warning' : 'success'}>
                    {gap > 0 ? won(gap) : `+${won(-gap)}`}
                  </Text>
                );
              },
            },
          ]}
        />
      </Card>

      <Row gutter={[16, 16]}>
        {/* ───────────────────── 제품별 ───────────────────── */}
        <Col xs={24} lg={10}>
          <Card
            size="small"
            title="제품별 매출"
            extra={
              <Text type="secondary" style={{ fontSize: 12 }}>
                발행액은 공급가액(부가세 제외)
              </Text>
            }
            style={{ height: '100%' }}
          >
            <Table<RevenueByProduct>
              rowKey="productCode"
              size="small"
              pagination={false}
              loading={byProduct.isLoading}
              dataSource={byProduct.data ?? []}
              locale={{ emptyText: '구독도 청구서도 없습니다.' }}
              columns={[
                {
                  title: '제품',
                  dataIndex: 'productName',
                  render: (name: string, r) => (
                    <div>
                      <div>{name}</div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        유료 {r.tenants}곳
                        {r.trialTenants > 0 && ` · 체험 ${r.trialTenants}곳`}
                      </Text>
                    </div>
                  ),
                },
                {
                  // 이미 일어난 일. 기간 안에 실제로 청구한 금액이다.
                  title: '발행액',
                  dataIndex: 'invoicedAmount',
                  width: 110,
                  align: 'right',
                  render: (v: number) => <strong>{won(v)}</strong>,
                },
                {
                  // 앞으로 들어올 돈. 체험은 아직 돈이 아니므로 따로 적는다.
                  title: '월 예상',
                  dataIndex: 'mrr',
                  width: 120,
                  align: 'right',
                  render: (v: number, r) => (
                    <div>
                      <div>{won(v)}</div>
                      {r.trialMrr > 0 && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          체험 +{won(r.trialMrr)}
                        </Text>
                      )}
                    </div>
                  ),
                },
              ]}
            />
          </Card>
        </Col>

        {/* ───────────────────── 연체 기업 ───────────────────── */}
        <Col xs={24} lg={14}>
          <Card
            size="small"
            style={{ height: '100%' }}
            title={
              <Space>
                <WarningOutlined style={{ color: '#cf1322' }} />
                <span>연체 기업</span>
              </Space>
            }
            extra={
              <Button
                icon={<DownloadOutlined />}
                onClick={() =>
                  downloadCsv('연체기업', overdue.data ?? [], [
                    { header: '기업', value: (r) => r.companyName },
                    { header: '식별자', value: (r) => r.slug },
                    { header: '상태', value: (r) => r.status },
                    { header: '연체금액', value: (r) => Math.round(r.overdueAmount) },
                    { header: '연체일수', value: (r) => r.overdueDays },
                    { header: '최초납기', value: (r) => csvDate(r.oldestDueAt) },
                    { header: '청구서수', value: (r) => r.invoiceCount },
                    { header: '자동정지', value: (r) => csvDate(r.autoSuspendedAt) },
                  ])
                }
              >
                엑셀
              </Button>
            }
          >
            <Table<OverdueTenant>
              rowKey="tenantId"
              size="small"
              pagination={false}
              loading={overdue.isLoading}
              dataSource={overdue.data ?? []}
              scroll={{ y: 280 }}
              locale={{ emptyText: '연체된 기업이 없습니다.' }}
              columns={[
                {
                  title: '기업',
                  key: 'company',
                  render: (_, r) => (
                    <Space direction="vertical" size={0}>
                      <span>{r.companyName}</span>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {r.slug}
                      </Text>
                    </Space>
                  ),
                },
                {
                  title: '연체 금액',
                  dataIndex: 'overdueAmount',
                  width: 120,
                  align: 'right',
                  render: (v: number) => <Text type="danger">{won(v)}</Text>,
                },
                {
                  title: '경과',
                  dataIndex: 'overdueDays',
                  width: 80,
                  align: 'right',
                  render: (v: number) => `${v}일`,
                },
                {
                  title: '상태',
                  key: 'state',
                  width: 110,
                  render: (_, r) =>
                    r.autoSuspendedAt ? (
                      <Tooltip
                        title={`${new Date(r.autoSuspendedAt).toLocaleDateString('ko-KR')} 자동 정지`}
                      >
                        <Tag color="red">자동 정지</Tag>
                      </Tooltip>
                    ) : r.status === 'SUSPENDED' ? (
                      <Tag>운영자 정지</Tag>
                    ) : (
                      <Tag color="orange">이용 중</Tag>
                    ),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </Space>
  );
}
